import { GeneralSetting, Ticket } from '@apis/index'
import {
  call,
  eye,
  outlineTicket,
  supportticket,
  whatsapp,
} from '@assets/icons'
import Button from '@components/button'
import Column from '@components/column'
import Fields from '@components/fields'
import { StyleLabel, StyleTextContaienr } from '@components/fields/style'
import Status from '@components/status'
import Table from '@components/table'
import { useModal } from '@context/ModalContext'
import styled from '@emotion/styled'
import { RootState } from '@store/index'
import theme from '@utils/theme'
import { Form, Formik } from 'formik'
import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

type TicketListProps = {
  title: string
  statusFilter: string
  editableStatuses?: string[]
  displayStatus?: (original: string) => string
}

const TicketList = ({
  title,
  statusFilter,
  editableStatuses = [],
  displayStatus = (s) => s,
}: TicketListProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [issueTitle, setIssueTitle] = useState('')
  const [issueTitles, setIssueTitles] = useState<any[]>([])

  const { showModal } = useModal()
  const {
    items: tickets = [],
    totalItems,
    totalPages,
    loading: tableLoading,
  } = useSelector((state: RootState) => state.tickets)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const fetchIssueTitles = async () => {
    try {
      const res = await GeneralSetting.getByGroup({
        id: 'issue-titles',
        state: 'ticketIssueTitles',
      })
      console.log(res, 'res')
      if (res) {
        setIssueTitles(res)
      }
    } catch (error) {
      console.error('Failed to fetch issue titles', error)
    }
  }

  const fetchTickets = async (search = '', titleFilter = '') => {
    try {
      setLoading(true)
      await Ticket.list({
        state: 'tickets',
        params: {
          page,
          limit: 30,
          status: statusFilter,
          search,
          issueTitle: titleFilter || undefined,
        },
      })
    } catch (error) {
      console.error(`Failed to fetch ${statusFilter} tickets`, error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchIssueTitles()
  }, [])

  useEffect(() => {
    setPage(1)
  }, [searchTerm, issueTitle])

  useEffect(() => {
    fetchTickets(searchTerm, issueTitle)
  }, [searchTerm, page, issueTitle, statusFilter])

  const columns = [
    {
      title: 'SN',
      key: 'sn',
      render: (_, i) => <p>{i}</p>,
    },
    {
      title: 'Created By',
      key: 'created_by',
      render: (item) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div>
            <p>{item.created_by?.name || 'Unknown User'}</p>
            <p>{item.created_by?.phone || 'N/A'}</p>
          </div>
        </div>
      ),
    },
    {
      title: 'Email',
      key: 'email',
      render: (item) => item.email || 'N/A',
    },
    {
      title: 'Reported On',
      key: 'reported_on',
      render: (item) => (
        <Column stylecolumn="row-gap:2px">
          <p>{item.created_date}</p>
          <p style={{ color: 'gray', fontSize: '12px' }}>{item.created_time}</p>
        </Column>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (item) => (
        <Status
          item={{ ...item, status: displayStatus(item.status) }}
          statuses={editableStatuses}
          onStatusChange={async (newStatus) => {
            const final = newStatus === 'Solved' ? 'Resolved' : newStatus
            await Ticket.update({
              state: 'tickets',
              id: item.id,
              data: { status: final },
            })
          }}
        />
      ),
    },
  ]

  const data = tickets.map((pt) => ({
    id: pt?.id,
    created_by: {
      name: pt?.user?.name || 'Unknown',
      phone: pt?.user?.phoneNo || 'N/A',
      avatar:
        `https://admin.tournaop.com/${pt?.user?.avatar}` ||
        'https://randomuser.me/api/portraits/women/44.jpg',
    },
    email: pt?.user?.email || 'N/A',
    created_date: pt.createdAt ? pt.createdAt.split('T')[0] : 'N/A',
    created_time: pt.createdAt
      ? (() => {
          const [hour, min, sec] = pt.createdAt
            .split('T')[1]
            .split('.')[0]
            .split(':')
          const h = Number(hour)
          const ampm = h >= 12 ? 'PM' : 'AM'
          const hour12 = h % 12 === 0 ? 12 : h % 12
          return `${hour12}:${min}:${sec} ${ampm}`
        })()
      : 'N/A',
    status: pt?.status,
  }))

  return (
    <Fragment>
      <Table
        onSearchChange={(val) => {
          setSearchTerm(val)
        }}
        loading={tableLoading}
        text={loading ? `Loading ${title.toLowerCase()}...` : title}
        searchKeys={['name', 'number']}
        data={data}
        columns={columns}
        icon={outlineTicket}
        totalItems={totalItems}
        totalPages={totalPages}
        pageControlled={page}
        onPageChange={setPage}
        itemsPerPage={30}
        rightSearchElement={
          <div>
            <Formik
              initialValues={{ issueTitle: issueTitle }}
              onSubmit={() => {}}
              enableReinitialize
            >
              <Fields.DropDown
                name="issueTitle"
                placeholder="Filter by Issue Title"
                options={[
                  ...issueTitles.map((t) => ({ title: t.value, key: t.value })),
                ]}
                titleAsKey="title"
                valueAsKey="key"
                onChange={(val) => {
                  setIssueTitle(val)
                }}
                styleWrapper="margin-bottom: 0; min-width: 180px; z-index:999999999999999"
              />
            </Formik>
          </div>
        }
        actions={[
          {
            key: 'view',
            icon: eye,
            onClick: (item) => {
              const fullData = tickets.find((ft) => ft.id === item.id)
              // console.log(fullData, 'fulldata')
              showModal(<TicketDetails data={fullData} />)
            },
          },
          {
            key: 'whatsapp',
            icon: whatsapp,
            onClick: (item) => {
              const phone = item?.created_by?.phone || '+9779860678385'
              const cleanPhone = phone.replace(/\D/g, '')
              window.open(`https://wa.me/${cleanPhone}`, '_blank')
            },
          },
          {
            key: 'call',
            icon: call,
            onClick: (item) => {
              const phone = item?.created_by?.phone || '+9779860678385'
              window.open(`tel:${phone}`, '_self')
            },
          },
        ]}
      />
    </Fragment>
  )
}

export default TicketList

const TicketDetails = ({ data }: { data: any }) => {
  const [initialValues, setInitialValues] = useState({
    remarks: '',
  })
  useEffect(() => {
    if (data.infos.remarks) {
      setInitialValues({ remarks: data.infos.remarks })
    }
  }, [data])

  const { hideModal } = useModal()
  return (
    <Fragment>
      <Column>
        <Title>Ticket Details</Title>
        <StyleLabel>Issue Title</StyleLabel>
        <StyleTextContaienr>{data.title}</StyleTextContaienr>
        <StyleLabel>Description</StyleLabel>
        <StyleTextContaienr>{data.description}</StyleTextContaienr>
        <StyleLabel>Screenshots</StyleLabel>
        <ScreenshotpreviewWrapper>
          {Array.isArray(data?.infos?.images) &&
          data.infos.images.length > 0 ? (
            data.infos.images.map((img, i) => (
              <Screenshotpreview
                key={i}
                src={
                  `https://admin.tournaop.com/${img}` ||
                  'https://randomuser.me/api/portraits/women/44.jpg'
                }
                alt={`screenshot-${i}`}
              />
            ))
          ) : (
            <StyleTextContaienr>No screenshots available.</StyleTextContaienr>
          )}
        </ScreenshotpreviewWrapper>
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={async (values) => {
            await Ticket.update({
              state: 'tickets',
              data: { remarks: values.remarks, status: 'Resolved' },
              id: data.id,
            }).then(() => {
              hideModal()
            })
          }}
        >
          <Form>
            <Column>
              <Fields.TextArea
                name="remarks"
                label="Add Remarks"
                placeholder="Enter Admin Remarks"
              />
              <Button
                type="submit"
                title="Solve Ticket"
                width={100}
                height={45}
              />
            </Column>
          </Form>
        </Formik>
      </Column>
    </Fragment>
  )
}

const Title = styled.p`
  color: ${theme.colors.primary};
  text-align: center;
  font-size: 18px;
`
const ScreenshotpreviewWrapper = styled.div`
  display: flex;
  gap: 12px;
  max-width: 100%;
  overflow-x: auto;
`
const Screenshotpreview = styled.img`
  border: 2px dashed ${theme.colors.borderColor};
  border-radius: 10px;
  flex: 1;
  object-fit: contain;
  max-height: 230px;
`
